<template>
  <div class="prizes-tab">
    <div class="info-card">
      <v-form class="awards-form">
        <draggable
          v-model="localAwards"
          @start="dragStart"
          @end="dragEnd"
          @change="handleChange"
          handle=".drag-handle"
          :animation="200"
          ghost-class="ghost-award"
          drag-class="dragging-award"
          :move="checkMove"
          :disabled="isTournamentStarted"
          tag="div"
        >
          <div
            v-for="(award, index) in sortedAwards"
            :key="award.id"
            class="award-item"
          >
            <!-- Left: drag + number -->
            <div class="award-left">
              <div class="drag-handle" :class="{ 'disabled': isTournamentStarted }">
                <i class="fa-regular fa-grip-vertical"></i>
              </div>
              <div class="award-index">{{ index + 1 }}º</div>
            </div>

            <!-- Middle: inputs -->
            <div class="award-content">
              <!-- TOP ROW -->
              <div class="top-row">
                <!-- Prize Name -->
                <div class="field-group">
                  <label class="field-title">
                    {{ $t("prizeName") }} <span class="required">*</span>
                  </label>
                  <v-text-field
                    v-model="award.name"
                    :error-messages="getErrorMessage(award, 'name')"
                    outlined
                    dense
                    hide-details="auto"
                    :disabled="isTournamentStarted"
                    :counter="maxAwardTextLength"
                    @input="handleInput(index, 'name')"
                    @blur="handleBlur(index, 'name')"
                  />
                </div>

                <!-- Estimated Value + Actions (FIX) -->
                <div class="value-actions">
                  <div class="field-group">
                    <label class="field-title">
                      {{ $t("estimatedValue") }}
                      <span class="required">*</span>
                    </label>
                    <v-text-field
                      v-model="award.value"
                      type="number"
                      :error-messages="getErrorMessage(award, 'value')"
                      outlined
                      dense
                      hide-details="auto"
                      prefix="$"
                      :disabled="isTournamentStarted"
                      @input="handleInput(index, 'value')"
                      @blur="handleBlur(index, 'value')"
                    />
                  </div>

                  <div class="award-actions">
                    <v-btn
                      v-if="index > 0"
                      icon
                      small
                      class="icon-btn delete"
                      :disabled="isTournamentStarted"
                      @click="removeAward(index)"
                    >
                      <i class="fa-regular fa-trash"></i>
                    </v-btn>

                    <v-btn
                      v-if="index === awards.length - 1"
                      icon
                      small
                      class="icon-btn add"
                      :disabled="isTournamentStarted"
                      @click="addAward"
                    >
                      <i class="fa-regular fa-plus"></i>
                    </v-btn>
                  </div>
                </div>
              </div>

              <!-- BOTTOM ROW -->
              <div class="bottom-row">
                <div class="field-group">
                  <label class="field-title">
                    {{ $t("description_TournamentAwards") }}
                    <span class="required">*</span>
                  </label>

                  <v-text-field
                    v-model="award.description"
                    :error-messages="getErrorMessage(award, 'description')"
                    outlined
                    dense
                    hide-details="auto"
                    :counter="maxAwardTextLength"
                    :disabled="isTournamentStarted"
                    @input="handleInput(index, 'description')"
                    @blur="handleBlur(index, 'description')"
                  />
                </div>
              </div>
            </div>

            <!-- Separator -->
            <div v-if="index !== sortedAwards.length - 1" class="separator">
              <span class="separator-plus">+</span>
            </div>
          </div>
        </draggable>
      </v-form>
    </div>
  </div>
</template>


<script>
import { mapState, mapMutations } from "vuex";
import draggable from "vuedraggable";
import Repository from "../../../../repositories/tournament";

export default {
  name: "PrizesTab",
  components: { draggable },
  props: {
    setNextBtnDisabled: { type: Function, required: false }
  },
  data: () => ({
    isDragging: false,
    initialValues: null,
    maxAwardTextLength: 35,
  }),
  computed: {
    tournamentSummary() {
      return this.$store.state.tournament;
    },
    isTournamentStarted() {
      if (!this.tournamentSummary || !this.tournamentSummary.startTime) return false;
      const now = new Date();
      const startTime = new Date(this.tournamentSummary.startTime);
      return now >= startTime;
    },
    ...mapState({
      awards: (state) => state.tournamentForm.awards,
    }),
    localAwards: {
      get() {
        return this.awards;
      },
      set(value) {
        this.updateTournamentAwards(value);
      },
    },
    sortedAwards() {
      return [...this.localAwards].sort((a, b) => (a.position || 1) - (b.position || 1));
    },
  },
  methods: {
    ...mapMutations(["updateTournamentAwards", "setTournament"]),

    init() {
      if (!this.tournamentSummary || !this.tournamentSummary.awards) {
        // If no awards in tournament, add one empty award
        if (!this.awards || this.awards.length === 0) {
          this.addAward();
        }
        // Store initial values after awards are set
        this.$nextTick(() => {
          this.saveInitialValues();
        });
        return;
      }

      // Map tournament awards to tournamentForm format
      const tournamentAwards = this.tournamentSummary.awards || [];
      const mappedAwards = tournamentAwards.map((award, index) => ({
        id: this.generateUniqueId(),
        position: award.rank || index + 1,
        name: award.title || award.name || '',
        description: award.description || '',
        value: award.estimatedValue || award.value || '',
        isDirty: { name: false, description: false, value: false },
        errors: {},
      }));

      // Update tournamentForm awards
      if (mappedAwards.length > 0) {
        this.updateTournamentAwards(mappedAwards);
      } else {
        // If no awards, add one empty award
        this.addAward();
      }

      // Store initial values for change detection after awards are updated
      this.$nextTick(() => {
        this.saveInitialValues();
        this.validateAllAwards();
      });
    },

    saveInitialValues() {
      // Create a normalized copy of awards for comparison (without isDirty and errors)
      const normalizedAwards = this.awards.map(award => ({
        position: award.position,
        name: (award.name || '').trim(),
        description: (award.description || '').trim(),
        value: (award.value || '').toString().trim(),
      }));
      // Sort by position to ensure consistent comparison
      normalizedAwards.sort((a, b) => (a.position || 0) - (b.position || 0));
      this.initialValues = JSON.stringify(normalizedAwards);
    },

    generateUniqueId() {
      return `award-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    },

    updatePositions(awards) {
      return awards.map((award, index) => ({
        ...award,
        id: award.id || this.generateUniqueId(),
        position: index + 1,
      }));
    },

    addAward() {
      const currentAwards = [...this.awards];
      const newAward = {
        id: this.generateUniqueId(),
        position: currentAwards.length + 1,
        name: "",
        description: "",
        value: "",
        isDirty: { name: false, description: false, value: false },
        errors: {},
      };

      const updated = this.updatePositions([...currentAwards, newAward]);
      this.updateTournamentAwards(updated);
      this.validateAllAwards();
      this.checkForChanges();
    },

    removeAward(index) {
      const current = [...this.localAwards];
      current.splice(index, 1);
      const updated = this.updatePositions(current);
      this.updateTournamentAwards(updated);
      this.$nextTick(() => {
        this.validateAllAwards();
        this.checkForChanges();
      });
    },

    handleInput(index, field) {
      const award = this.awards[index];
      award.isDirty[field] = true;
      this.validateAward(award);
      this.validateAllAwards();
      this.checkForChanges();
    },

    handleBlur(index, field) {
      this.handleInput(index, field);
    },

    getErrorMessage(award, field) {
      return award.isDirty?.[field] ? award.errors?.[field] || "" : "";
    },

    validateAward(award) {
      const errors = {};
      const max = this.maxAwardTextLength;

      const name = (award.name || "").trim();
      const desc = (award.description || "").trim();

      if (!name) errors.name = this.$t("errors.required");
      else if (name.length > max) errors.name = this.$t("AwardNameError", { max });

      if (!desc) errors.description = this.$t("errors.required");
      else if (desc.length > max) errors.description = this.$t("AwardDescriptionError", { max });

      const value = parseFloat(award.value);
      if (award.value === "" || award.value === null || award.value === undefined) {
        errors.value = this.$t("errors.required");
      } else if (isNaN(value) || value < 0) {
        errors.value = this.$t("errors.greaterThanZero");
      }

      award.errors = errors;
      return Object.keys(errors).length === 0;
    },

    validateAllAwards() {
      if (!this.awards || this.awards.length === 0) {
        return false;
      }

      const ok = this.awards.every((a) => !a.isDirty || this.validateAward(a));
      return ok;
    },

    checkForChanges() {
      if (!this.initialValues) {
        this.setNextBtnDisabled?.(true);
        return;
      }

      // Create a normalized copy of current awards for comparison (without isDirty and errors)
      const normalizedAwards = this.awards.map(award => ({
        position: award.position,
        name: (award.name || '').trim(),
        description: (award.description || '').trim(),
        value: (award.value || '').toString().trim(),
      }));
      // Sort by position to ensure consistent comparison
      normalizedAwards.sort((a, b) => (a.position || 0) - (b.position || 0));
      const currentValues = JSON.stringify(normalizedAwards);
      
      const hasChanges = currentValues !== this.initialValues;
      const isValid = this.validateAllAwards();

      // Disable if no changes or form is invalid
      this.setNextBtnDisabled?.(!hasChanges || !isValid);
    },

    checkMove(evt) {
      return !!(evt.draggedContext.element && evt.relatedContext.element);
    },

    dragStart() {
      this.isDragging = true;
      document.body.classList.add("dragging-active");
    },

    dragEnd(evt) {
      this.isDragging = false;
      document.body.classList.remove("dragging-active");

      if (evt.oldIndex !== evt.newIndex) {
        const updated = this.updatePositions([...this.localAwards]);
        this.$nextTick(() => {
          this.updateTournamentAwards(updated);
          this.validateAllAwards();
          this.checkForChanges();
        });
      }
    },

    handleChange() {
      const updated = this.updatePositions([...this.localAwards]);
      this.$nextTick(() => {
        this.updateTournamentAwards(updated);
        this.checkForChanges();
      });
    },

    saveTournamentSettings() {
      // Validate that tournament exists in store
      if (!this.tournamentSummary || !this.tournamentSummary.tournamentId) {
        this.$emit('save-error', {
          message: this.$t('alerts.errorOccurred') || 'Tournament data not available'
        });
        return;
      }

      // Validate all awards before saving
      if (!this.validateAllAwards()) {
        this.$emit('save-error', {
          message: this.$t('alerts_TournamentSettings.invalidForm') || 'Please fill all required fields'
        });
        return;
      }

      // Transform awards from frontend format to backend format
      const tournamentAwards = this.sortedAwards.map(award => ({
        rankNumber: award.position || 1,
        title: award.name || '',
        description: award.description || '',
        estimatedValue: `${award.value || 0}`,
        percentagePoolsize: 0.0 // Default value, can be calculated if needed
      }));

      const prizesData = {
        tournamentId: this.tournamentSummary.tournamentId,
        tournamentAwards: tournamentAwards
      };

      Repository.editTournamentPrizes(prizesData).then((response) => {
        if (response.status < 400 && response.data) {
          // Update initial values after successful save
          this.saveInitialValues();
          
          // Disable save button after successful save
          if (this.setNextBtnDisabled) {
            this.setNextBtnDisabled(true);
          }

          // Refresh tournament data and update store
          Repository.getTournamentOverview(this.tournamentSummary.tournamentId).then((response) => {
            if (response.status < 400) {
              this.setTournament(response.data);
            }
          });

          // Emit success event
          this.$emit('save-success', {
            message: this.$t('alerts_TournamentSettings.edited') || 'Prizes updated successfully'
          });
        } else {
          this.$emit('save-error', {
            message: this.$t('alerts.errorOccurred') || 'Error updating prizes'
          });
        }
      }).catch((error) => {
        this.$emit('save-error', {
          message: error.response?.data?.message || this.$t('alerts.errorOccurred') || 'Error updating prizes'
        });
      });
    },
  },

  mounted() {
    if (this.tournamentSummary) {
      this.init();
    }
    // Disable save button initially
    if (this.setNextBtnDisabled) {
      this.setNextBtnDisabled(true);
    }
  },
  watch: {
    tournamentSummary: {
      handler(newVal) {
        if (newVal && !this.initialValues) {
          this.init();
        }
      },
      immediate: true
    }
  },
};
</script>


<style lang="scss" scoped>
.info-card {
  background-color: var(--v-cardBackground-base);
  border: 1px solid #ededed;
  border-radius: 10px;
  padding: 24px 24px 18px;
}

.theme--dark .info-card {
  border-color: #424242;
}

.award-item {
  display: grid;
  grid-template-columns: 64px 1fr; // ya NO hay columna externa de acciones
  gap: 12px;
  align-items: start;
  padding: 12px 0;
}

.award-left {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 24px;
}

.drag-handle {
  cursor: grab;
  opacity: 0.6;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }

  i {
    color: #9a9a9a;
    font-size: 16px;
  }

  &:active {
    cursor: grabbing;
  }
}

.award-index {
  font-weight: 700;
  color: var(--v-textPrimary-base);
}

.award-content {
  min-width: 0; // clave para que el grid no "empuje" y genere overlays
}

/* TOP ROW: Name + (Value + Actions) */
.top-row {
  display: grid;
  grid-template-columns: 1fr 340px; // ajusta si lo necesitas
  gap: 12px;
  align-items: start;
}

/* Value input + buttons */
.value-actions {
  display: grid;
  grid-template-columns: 1fr 84px; // 84px = dos botones (2*34 + gap)
  gap: 10px;
  align-items: end;
  min-width: 0;
}

.value-actions .v-text-field {
  min-width: 0; // evita que el input se meta debajo de los botones
}

.award-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-bottom: 2px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-title {
  font-family: "Avenir", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--v-textPrimary-base);
}

.required {
  color: red;
  margin-left: 2px;
}

.bottom-row {
  margin-top: 10px;
}

/* Buttons */
.icon-btn {
  width: 34px !important;
  height: 34px !important;
  border-radius: 8px !important;
}

.icon-btn.delete {
  background: #9a9a9a !important;
  transition: background 0.2s ease;

  i {
    color: white;
  }

  &:hover {
    background: #757575 !important;
  }
}

.icon-btn.add {
  background: var(--v-mainGreen-base) !important;
  transition: background 0.2s ease;

  i {
    color: black;
  }

  &:hover {
    background: #20c77d !important;
  }
}

/* Separator */
.separator {
  grid-column: 1 / -1;
  position: relative;
  margin: 14px 0 6px;
  height: 1px;
  background: #d7d7d7;
}

.separator-plus {
  position: absolute;
  left: 50%;
  top: -10px;
  transform: translateX(-50%);
  background: var(--v-cardBackground-base);
  padding: 0 8px;
  color: #9a9a9a;
  font-weight: 700;
}

.theme--light .prizes-tab {
  --disabled-bg: #f5f5f5;
  --disabled-border: #e0e0e0;
  --disabled-text: #9e9e9e;
  --disabled-icon: #9e9e9e;
}

.theme--dark .prizes-tab {
  --disabled-bg: rgba(255, 255, 255, 0.06);
  --disabled-border: rgba(255, 255, 255, 0.14);
  --disabled-text: rgba(255, 255, 255, 0.55);
  --disabled-icon: rgba(255, 255, 255, 0.45);
}

::v-deep .v-input--is-disabled .v-input__control .v-input__slot {
  background-color: var(--disabled-bg) !important;
  border-color: var(--disabled-border) !important;
}

::v-deep .v-input--is-disabled input,
::v-deep .v-input--is-disabled textarea {
  color: var(--disabled-text) !important;
  -webkit-text-fill-color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-label {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-select__selection {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-icon {
  color: var(--disabled-icon) !important;
}

::v-deep .v-input--is-disabled .v-input__details .v-messages .v-messages__message {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-input__details .v-counter {
  color: var(--disabled-text) !important;
}

/* Responsive Design para móviles y tablets */
@media (max-width: 960px) {
  .award-item {
    grid-template-columns: 40px 1fr;
    gap: 8px;
  }

  .award-left {
    padding-top: 20px;
    gap: 4px;
  }

  .award-index {
    font-size: 14px;
  }

  /* Top row: cambiar a columna en mobile */
  .top-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  /* Value y acciones: cambiar a columna */
  .value-actions {
    grid-template-columns: 1fr;
    gap: 8px;
    align-items: stretch;
  }

  .award-actions {
    justify-content: flex-start;
    padding-bottom: 0;
  }

  .info-card {
    padding: 16px 12px 12px;
  }
}

@media (max-width: 600px) {
  .drag-handle {
    display: none;
  }

  .award-item {
    grid-template-columns: 32px 1fr;
  }

  .award-left {
    padding-top: 24px;
  }

  .icon-btn {
    width: 40px !important;
    height: 40px !important;
  }
}
</style>

